import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ChevronRight, Home } from 'lucide-react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  current?: boolean;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  className?: string;
}

export function Breadcrumb({ 
  items, 
  separator = '/', 
  className 
}: BreadcrumbProps) {
  return (
    <nav aria-label="Breadcrumb" className={cn('flex', className)}>
      <ol className="flex items-center space-x-2 text-sm">
        {items.map((item, index) => (
          <li key={index} className="flex items-center">
            {index > 0 && (
              <span className="mx-2 text-muted-foreground/60" aria-hidden="true">
                {separator}
              </span>
            )}
            {item.current || !item.href ? (
              <span
                className={cn(
                  'font-medium',
                  item.current ? 'text-foreground' : 'text-muted-foreground'
                )}
                aria-current={item.current ? 'page' : undefined}
              >
                {item.label}
              </span>
            ) : (
              <Link
                to={item.href}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export interface BreadcrumbWithIconsProps extends BreadcrumbProps {
  homeIcon?: React.ReactNode;
}

export function BreadcrumbWithIcons({
  items,
  separator = <ChevronRight className="w-4 h-4 text-muted-foreground/60" />,
  homeIcon = <Home className="w-4 h-4" />,
  className,
}: BreadcrumbWithIconsProps) {
  const itemsWithHome = items[0]?.label === 'Home' 
    ? [{ ...items[0], icon: homeIcon }, ...items.slice(1)]
    : items;

  return (
    <nav aria-label="Breadcrumb" className={cn('flex', className)}>
      <ol className="flex items-center space-x-1 text-sm">
        {itemsWithHome.map((item: any, index) => (
          <li key={index} className="flex items-center">
            {index > 0 && (
              <span className="mx-1" aria-hidden="true">
                {separator}
              </span>
            )}
            {item.current || !item.href ? (
              <span
                className={cn(
                  'font-medium flex items-center gap-1',
                  item.current ? 'text-foreground' : 'text-muted-foreground'
                )}
                aria-current={item.current ? 'page' : undefined}
              >
                {item.icon}
                {item.label !== 'Home' && item.label}
              </span>
            ) : (
              <Link
                to={item.href}
                className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                {item.icon}
                {item.label !== 'Home' && item.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
